// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { MathUtils } from '@shared/utils/MathUtils';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { Resources, CurrentLang } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { TradingMode } from '@shared/utils/Instruments/TradingMode';
import { TradingPositionType } from '@shared/utils/Instruments/TradingPositionType';
import { QuotingType } from '@shared/utils/Instruments/QuotingType';
import { CurPriceSource } from '@shared/utils/Instruments/CurPriceSource';
import { CouponCycle } from '@shared/utils/Instruments/CouponCycle';
import { CONTINUOUS_CONTRACT_POSTFIX, SessionOperations } from '@shared/utils/Enums/Constants';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { AccountTradeStatus } from '@shared/utils/Account/AccountTradeStatus';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { InstrumentTradingBalance } from '@shared/utils/Instruments/InstrumentTradingBalance';
import { InstrumentSpecificType } from '@shared/utils/Instruments/InstrumentSpecificType';
import { Periods } from '@shared/utils/History/TFInfo';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { RiskSettings } from './RiskSettings';
import { Limits } from './Limits';
import { Level1Calculator } from './Level1Calculator';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { InterTraderBtnStatus } from '@shared/utils/Instruments/InterTraderBtnStatus';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { VariableTick } from '@shared/utils/Instruments/VariableTick';
import { FixedIncomePricingType, EXT_INSTRUMENT_MESSAGE, ExerciseStyle } from './InstrumentConstants';
import { WarningForChangeFromLastPrice } from './WarningForChangeFromLastPrice';
// need convert DirectMessages to ts for using import { DirectInstrumentDayBarMessage, DirectInstrumentMessage } from '../../Utils/DirectMessages/DirectMessagesImport';
import { DirectInstrumentDayBarMessage } from '@shared/utils/DirectMessages/DirectInstrumentDayBarMessage';
import { DirectInstrumentMessage } from '@shared/utils/DirectMessages/DirectInstrumentMessage';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { GlobalFlags } from '../UtilsClasses/GlobalFlags';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { InstrumentDayInfo } from '@shared/utils/Instruments/InstrumentDayInfo';
import { VolumeHistoryMode } from '@shared/utils/Volume/VolumeConstantsAndEnums';
import { PositionsTotalInfo } from './PositionsTotalInfo';
import { DataCache } from '../DataCache';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
import { OptionType } from '@shared/utils/Instruments/OptionType';
import { DayPeriods } from '@shared/utils/Session/DayPeriods';
import { QuoteDelay } from './QuoteDelay';
import { CrossRateKey } from './CrossRateKey';
import { SymbolLookupViewItemHelper } from '@shared/utils/Instruments/SymbolLookupViewItemHelper';
import { ticksUtils } from '@shared/utils/Trading/TicksUtils';
// TODO. Multi select ItemALL refactoring.
// TODO.TODO.TODO.TODO.TODO.TODO.TODO.
export class Instrument {
    get InstrType() {
        return this._instrType;
    }
    set InstrType(value) {
        switch (value) {
            case InstrumentTypes.EQUITIES_CFD:
                this._instrType = InstrumentTypes.EQUITIES;
                this.CFD = true;
                break;
            case InstrumentTypes.CFD_FUTURES:
                this._instrType = InstrumentTypes.FUTURES;
                this.CFD = true;
                break;
            default:
                this._instrType = value;
                break;
        }
    }
    get isUnderlier() {
        return this._isOptions || isValidArray(this._underlierOptionInfo);
    }
    constructor(dataCache, mess, route) {
        this.UseOptionDescription = null;
        this.ForwardBaseInstrument = null;
        this.ForwardBaseInstrumentId = undefined;
        this.ExpDate = null;
        this._cachedInteriorID = null;
        this.UnderlierDescription = '';
        this.Precision = -1;
        this.AssetName = null;
        this.EnableComment = false;
        // то, что пришло от сервера, т.е. константа серверная. При вызове
        // HistoryType заменяется на протрейдеровскую
        this.HistoryType = 0;
        // настройка для отображения чарта
        this.DefaultChartHistoryType = 0;
        this.lotStepPrecisionCache = new Map();
        this.TradingMode = TradingMode.FullyClosed;
        this.routeName = '';
        this.DisplayFullName = '';
        this.CurPriceSource = CurPriceSource.Level1;
        this.BidOpen = Number.NaN;
        this.BidHigh = Number.NaN;
        this.BidLow = Number.NaN;
        this.BidPrevClose = Number.NaN;
        this.AskOpen = Number.NaN;
        this.AskHigh = Number.NaN;
        this.AskLow = Number.NaN;
        this.AskPrevClose = Number.NaN;
        this.Ticks = 0;
        this.TicksPreMarket = 0;
        this.TicksPostMarket = 0;
        this.VolumePreMarket = 0;
        this.VolumePostMarket = 0;
        this.OpenInterest = 0;
        this.SettlementPrice = NaN;
        this.PrevSettlementPrice = NaN;
        this.BidMainClose = NaN;
        this.AskMainClose = NaN;
        this.Open = Number.NaN;
        this.Close = Number.NaN;
        this.High = Number.NaN;
        this.Low = Number.NaN;
        this.Volume = 0;
        this.TotalVolume = Number.NaN;
        this.OHLC_BidHigh = Number.NaN; // Обработка месседжа котировки дополнительно происходит в Instrument.OHLCCalculator, появилось с необходимостью получения корректных значений DayHigh/Low #91760
        this.OHLC_BidLow = Number.NaN;
        this.OHLC_AskHigh = Number.NaN;
        this.OHLC_AskLow = Number.NaN;
        this.OHLC_TradeHigh = Number.NaN;
        this.OHLC_TradeLow = Number.NaN;
        this.FiftyTwoWeekHighPrice = null;
        this.FiftyTwoWeekLowPrice = null;
        this.High13Week = null;
        this.Low13Week = null;
        this.High26Week = null;
        this.Low26Week = null;
        // TODO. Refactor.
        this.FirstQuote = null;
        this.LastQuote = null;
        this.LastInvalidQuote = null;
        this.exchangeSessionName = null;
        this.TradeSessionStatusId = null;
        this.QuotingType = QuotingType.None;
        this.InstrumentAdditionalInfo = {};
        this.pipsSizeSpecified = null;
        this.InstrumentTradableID = null;
        this.FLanguageAliases = null;
        this.TradingBalance = 0;
        this.ExchangeSessionNameUpdated = new CustomEvent();
        this.NewQuote = new CustomEvent();
        this.RiskSettingsUpdated = new CustomEvent();
        this.ExpDateReal = DateTimeUtils._ZeroTime;
        this.InstrumentSpecificType = InstrumentSpecificType.None;
        this.HideAsset = true;
        this.Industry = '';
        this.Sector = '';
        this.IndustryCustomListId = null;
        this.SectorCustomListId = null;
        this.ShortPositionInterest = null;
        this.DailyAvailableForShort = null;
        this.SaveQuotesHistory = null;
        this.UseSessionPeriodForIntradayChart = null;
        this.ISIN = '';
        this.LogoAddress = null;
        this.MarketCap = 0;
        this.ContractSize = 1;
        this.ExchangeId = null;
        this.TradingExchange = null;
        this.MarketDataExchange = null;
        this.CountryId = -1;
        this.ExerciseStyle = null;
        this.SeriesDescription = '';
        this.WarningForChangeFromLastPrice = null; // https://tp.traderevolution.com/entity/108864
        this.IsAllowCustomSLTPTriggerPrice = null; // https://tp.traderevolution.com/entity/109798
        this.AllowExerciseForItmOption = false;
        this.PrevPaymentDate = DateTimeUtils._ZeroTime;
        this.ContractMonthDate = DateTimeUtils._ZeroTime;
        this.FirstTradeDate = DateTimeUtils._ZeroTime;
        this.NoticeDate = DateTimeUtils._ZeroTime;
        this.PutCall = OptionPutCall.OPTION_NONE;
        this.StrikeVersion = null;
        this.SettlementDate = null;
        this.HightLimit = undefined;
        this.LowLimit = undefined;
        this.CFD = false;
        this.PricingType = null;
        // задержка котировок
        this.QuoteDelay = new QuoteDelay(); // more details in #101065
        this.QuoteCanGetSnapshot = null;
        this.HasLvl1SubscriptionErrorCode = null;
        this.DataSourceTradableId = null;
        this.DataSourceRouteId = null;
        this.DataSourceInstrument = null;
        this.optionType = OptionType.None;
        this.PositionsTotalInfo = new PositionsTotalInfo();
        this.haveContiniousContract = false;
        this._isOptions = false;
        this._underlierOptionInfo = [];
        this.isAllowOTHTrading = null;
        this.DataCache = dataCache;
        this.Level1 = new Level1Calculator(this);
        this.DataSourceLevel1 = new Level1Calculator(this);
        this.InstrumentDayInfo = new InstrumentDayInfo(this);
        this.ShortName = mess.Name || '';
        this.FullName = InstrumentUtils.GetFullName(this.ShortName, route);
        this.Route = route;
        // TODO.
        this.InstrumentDayBarMessageUpdateMode = false;
        let r = dataCache.Routes.getRouteById(route);
        if (r !== null) {
            this.InstrumentDayBarMessageUpdateMode = r.InstrumentDayBarMessageUpdateMode;
            this.RouteName = r.Name;
            this.FullName = InstrumentUtils.GetFullName(this.ShortName, r.Name);
            // 53234 - уточнил у Качура, для случая когда один роут инфо, а второй торговый, копируем настройку с инфо роута
            // исходя из комента выше, нужно добавить поиск роута с котировками в нашем случае
            if (r.QuoteRouteId !== r.RouteId) {
                r = dataCache.Routes.getRouteById(r.QuoteRouteId);
            }
            if (r !== null) {
                this.InstrumentDayBarMessageUpdateMode = r.InstrumentDayBarMessageUpdateMode;
            }
        }
        this.Limits = new Limits(this);
        this.RiskSettings = new RiskSettings(this);
        this.StrikePrice = DateTimeUtils._ZeroTime.getTime();
        this.UpdateByMessage(mess);
    }
    get MinDelta() {
        if (this.PointSize > 0)
            return this.MinimumPointSize();
        return 1;
    }
    get RouteName() {
        return this.routeName;
    }
    set RouteName(value) {
        this.routeName = value;
        this.DisplayFullName = InstrumentUtils.GetFullName(this.ShortName, value);
    }
    get InstrumentDayBarMessageUpdateMode() {
        return this._instrumentDayBarMessageUpdateMode;
    }
    set InstrumentDayBarMessageUpdateMode(value) {
        this._instrumentDayBarMessageUpdateMode = value;
        this.InstrumentDayInfo.InstrumentDayBarMessageUpdateMode = value;
    }
    get YieldRate() {
        return this._yieldRate;
    }
    set YieldRate(value) {
        if (!isValidNumber(value)) {
            return;
        }
        this._yieldRate = MathUtils.RoundDouble(value, 4);
    }
    get LocalizedIndustry() {
        return DataCache.customInstrumentListsCache.getInstrumentIndustry(this);
    }
    get LocalizedSector() {
        return DataCache.customInstrumentListsCache.getInstrumentSector(this);
    }
    DisplayName() {
        let alias = '';
        if (this.FLanguageAliases) {
            const languageAlias = this.FLanguageAliases[CurrentLang];
            if (languageAlias) {
                alias = languageAlias.Name;
            }
        }
        return (alias ||
            (this.isHideRouteMode ? this.ShortName : this.DisplayFullName ? this.DisplayFullName : this.FullName));
    }
    DisplayShortName() {
        let alias = '';
        if (this.FLanguageAliases) {
            const languageAlias = this.FLanguageAliases[CurrentLang];
            if (languageAlias) {
                alias = languageAlias.Name;
            }
        }
        return alias || this.ShortName;
    }
    LogoName() {
        let alias = null;
        if (this.FLanguageAliases) {
            alias = this.FLanguageAliases[CurrentLang];
        }
        if (alias === null || alias === void 0 ? void 0 : alias.Description) {
            return alias.Description;
        }
        if (this.Description) {
            return this.Description;
        }
        if (alias === null || alias === void 0 ? void 0 : alias.Name) {
            return alias.Name;
        }
        const name = this.isHideRouteMode
            ? this.ShortName
            : this.DisplayFullName
                ? this.DisplayFullName
                : this.FullName;
        if (this.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            return name[1];
        }
        return name;
    }
    DescriptionValue() {
        let langDesc = '';
        if (this.FLanguageAliases) {
            const languageAlias = this.FLanguageAliases[CurrentLang];
            if (languageAlias) {
                langDesc = languageAlias.Description;
            }
        }
        return langDesc || this.Description;
    }
    getInstrumentTypeName() {
        const t = this.DataCache.getInstrumentTypeById(this.TypeId);
        if (t !== null) {
            return t.DisplayName();
        }
        else {
            return '';
        } // return "Undefined";
    }
    getInstrumentGroupId() {
        const t = this.DataCache.getInstrumentTypeById(this.TypeId);
        if (t != null) {
            return t.TypeId;
        }
        else {
            return -1;
        }
    }
    setPrecision(mess) {
        this.Precision = mess.Precision;
        if (!this.Precision) {
            this.Precision = Instrument.CalculatePrecision(mess.PointSize, mess.VariableTickList);
        }
    }
    // TODO. Add remaining stuff.
    UpdateByMessage(mess) {
        var _a;
        if (mess.HightLimit >= 0) {
            this.Limits.HightLimit = mess.HightLimit;
        }
        if (mess.LowLimit >= 0) {
            this.Limits.LowLimit = mess.LowLimit;
        }
        if (mess.PriceLimitMeasure >= 0) {
            this.Limits.PriceLimitMeasure = mess.PriceLimitMeasure;
        }
        if (mess.IsHighLimitFrontEndValidationEnabled !== null) {
            this.Limits.IsHighLimitFrontEndValidationEnabled = mess.IsHighLimitFrontEndValidationEnabled;
        }
        if (mess.IsLowLimitFrontEndValidationEnabled !== null) {
            this.Limits.IsLowLimitFrontEndValidationEnabled = mess.IsLowLimitFrontEndValidationEnabled;
        }
        this.Level1.UseOnlyLast = mess.UseOnlyLast;
        this.Id = mess.Id;
        this.Description = mess.Descr;
        this.UnderlierDescription = mess.UnderlierDescription;
        this.PointSize = mess.PointSize;
        this.setPrecision(mess);
        this.PointSize = MathUtils.RoundDouble(this.PointSize, this.Precision);
        this.pipsSizeSpecified = mess.PipsSizeSpecified;
        this.AssetName = mess.AssetName;
        this.ShortSwapInfo = mess.ShortSwapInfo;
        this.SwapBuy = mess.SwapBuy;
        this.SwapSell = mess.SwapSell;
        this.SwapType = mess.SwapType;
        this.InstrType = mess.InstrType;
        this.TypeId = mess.TypeId;
        this.Exp1 = this.getExp1(mess.Exp1, mess.Exp2);
        this.Exp2 = this.getExp2(mess.Exp2);
        this.CrossRateKey = new CrossRateKey(this.Exp1, this.Exp2);
        this.asset_exp2 = this.DataCache.GetAssetByName(this.Exp2);
        this.ValueCrossRateForPositions = mess.ValueCrossRateForPositions;
        this.RiskSettings.Update(mess);
        this.HistoryType = mess.HistoryType;
        if (this.HistoryType === 0) {
            this.HistoryType = HistoryType.QUOTE_LEVEL1;
        }
        this.DefaultChartHistoryType = mess.DefaultChartHistoryType;
        if (this.DefaultChartHistoryType === 0) {
            this.DefaultChartHistoryType = this.HistoryType;
        }
        this.RoutesSupported = mess.RoutesSupported;
        this.RoutesSupportedArray = this.RoutesSupported; // Instrument.getSupportedRoutes(this.RoutesSupported);
        this.TradableRoutes = mess.TradableRoutes;
        this.TradableRoutesArray = this.TradableRoutes; // Instrument.getSupportedRoutes(this.TradableRoutes);
        if ((_a = mess.VariableTickList) === null || _a === void 0 ? void 0 : _a.length) {
            this.VariableTickList = mess.VariableTickList;
        }
        else {
            this.VariableTickList = [
                new VariableTick({
                    lowLimit: -Infinity,
                    highLimit: Infinity,
                    allowLimit: true,
                    pointSize: this.PointSize,
                    tickCost: 1.0
                })
            ];
        }
        // TODO. Sessions? Maybe "session"?
        this.TradingSessionsId = mess.TradingSessionsId;
        const session = this.DataCache.SessionCache.getSession(mess.TradingSessionsId);
        if (!isNullOrUndefined(session)) {
            this.ApplySession(session);
        }
        if (mess.ExchangeId !== null && mess.ExchangeId !== undefined) {
            this.ExchangeId = mess.ExchangeId;
        }
        this.TradingExchange = mess.TradingExchange;
        this.MarketDataExchange = mess.MarketDataExchange;
        this.CountryId = mess.CountryId;
        this.ExerciseStyle = mess.ExerciseStyle;
        this.lotStep = mess.LotStep;
        this.LotSize = mess.LotSize;
        this.MinLot = mess.MinimalLot;
        this.MaxLot = mess.MaxLot;
        this.MaxLotInCash = mess.MaxLotInCash;
        // TODO. Coast? Maybe "cost"?
        this.FuturesTickCoast = mess.FuturesTickCoast;
        this.ContractMultiplier = mess.ContractMultiplier || 1;
        this.TradingMode = mess.TradingMode;
        this.TradingPositionType = mess.TradingPositionType;
        // TODO.
        /* this.forwardBaseInstrument = mess.ForwardBaseInstrument;
         */
        this.UseOptionDescription = mess.UseOptionDescription;
        this.ForwardBaseInstrumentId = mess.ForwardBaseInstrumentID;
        // TODO. Wrong message field. Use mess.InstrumentAdditionalInfo instead.
        if (mess.AdditionalFields) {
            const additionalFields = mess.AdditionalFields;
            for (const key in additionalFields) {
                this.InstrumentAdditionalInfo[key] = additionalFields[key];
            }
        }
        this.AllExpDateLoaded = mess.AllExpDateLoaded;
        this.AllContractsLoaded = mess.AllContractsLoaded;
        // if (mess.QuotingType !== QuotingType.None)       // ДУБЛИКАТ
        //     this.QuotingType = mess.QuotingType;
        // else
        //     this.QuotingType = (this.InstrType === Instrument.FUTURES || this.InstrType === Instrument.CFD_FUTURES || this.InstrType === Instrument.OPTIONS || this.InstrType === Instrument.SPREADBET || this.InstrType === Instrument.FORWARD) ? QuotingType.TickCost_TickSize : QuotingType.LotSize;
        this.CurPriceSource = mess.curPriceSource;
        this.TradingBalance = mess.TradingBalance;
        this.DeliveryMethod = mess.DeliveryMethod;
        if (mess.ContractSize !== null) {
            this.ContractSize = mess.ContractSize;
        }
        this.TradeSessionStatusId = mess.TradeSessionStatusId || -1;
        this.ExchangeSessionName = mess.ExchangeSessionName;
        this.InstrDateSettingsList = mess.InstrDateSettingsList;
        if (mess.LanguageAliases !== null) {
            this.FLanguageAliases = Instrument.CloneLangAliases(mess.LanguageAliases);
        }
        if (mess.EnableComment !== null) {
            this.EnableComment = mess.EnableComment;
        }
        // TODO ?
        this.isOptionSymbol = this.InstrType === InstrumentTypes.OPTIONS;
        this.isFuturesSymbol =
            this.InstrType === InstrumentTypes.FUTURES || this.InstrType === InstrumentTypes.CFD_FUTURES;
        this.UpdateHideRouteMode();
        this.OptionTradingStyle = mess.OptionTradingStyle;
        const insType = this.InstrType;
        if (mess.QuotingType !== QuotingType.None) {
            this.QuotingType = mess.QuotingType;
        }
        else {
            this.QuotingType =
                insType === InstrumentTypes.FUTURES ||
                    insType === InstrumentTypes.CFD_FUTURES ||
                    insType === InstrumentTypes.OPTIONS ||
                    insType === InstrumentTypes.SPREADBET ||
                    insType === InstrumentTypes.FORWARD
                    ? QuotingType.TickCost_TickSize
                    : QuotingType.LotSize;
        }
        if (mess.InstrumentTradableID) {
            this.InstrumentTradableID = mess.InstrumentTradableID;
        }
        if (mess.DataSourceTradableId) {
            this.DataSourceTradableId = mess.DataSourceTradableId;
            this.DataSourceInstrument = null;
        }
        if (mess.DataSourceRouteId) {
            this.DataSourceRouteId = mess.DataSourceRouteId;
        }
        if (mess.LastTradeDate) {
            this.LastTradeDate = mess.LastTradeDate;
        }
        if (mess.MaturityDate) {
            this.MaturityDate = mess.MaturityDate;
        }
        if (mess.FaceValue) {
            this.FaceValue = mess.FaceValue;
        }
        if (mess.PricingType != null) {
            this.PricingType = mess.PricingType;
        }
        if (isValidNumber(mess.CouponRate)) {
            this.CouponRate = mess.CouponRate;
        }
        if (isValidNumber(mess.CouponCycle)) {
            this.CouponCycle = mess.CouponCycle;
        }
        if (isValidNumber(mess.YieldRate)) {
            this.YieldRate = mess.YieldRate;
        }
        if (mess.AccruedInterest) {
            this.AccruedInterest = mess.AccruedInterest;
        }
        if (mess.DaysPerMonth != null) {
            this.DaysPerMonth = mess.DaysPerMonth;
        }
        if (mess.DaysPerYear != null) {
            this.DaysPerYear = mess.DaysPerYear;
        }
        if (mess.PrevCouponDate) {
            this.PrevPaymentDate = mess.PrevCouponDate;
        }
        if (mess.NextCouponDate) {
            this.NextPaymentDate = mess.NextCouponDate;
        }
        this.ISIN = mess.ISIN;
        this.LogoAddress = mess.LogoAddress;
        this.MarketCap = mess.MarketCap;
        if (mess.Industry) {
            this.Industry = mess.Industry;
        }
        if (mess.Sector) {
            this.Sector = mess.Sector;
        }
        if (mess.IndustryCustomListId) {
            this.IndustryCustomListId = mess.IndustryCustomListId;
        }
        if (mess.SectorCustomListId) {
            this.SectorCustomListId = mess.SectorCustomListId;
        }
        // if (mess.ShortPositionInterest)                                   // 95609
        this.ShortPositionInterest = mess.ShortPositionInterest;
        // if (mess.DailyAvailableForShort)
        this.DailyAvailableForShort = mess.DailyAvailableForShort;
        this.SaveQuotesHistory = mess.SaveQuoteHistory;
        this.UseSessionPeriodForIntradayChart = mess.UseSessionPeriodForIntradayChart;
        const warningForChangeFromLastPrice = this.GetWarningForChangeFromLastPrice(mess.WarningForChangeFromLastPriceRanges);
        if (warningForChangeFromLastPrice) {
            this.WarningForChangeFromLastPrice = warningForChangeFromLastPrice;
        }
        if (mess.IsAllowCustomSLTPTriggerPrice !== null) {
            this.IsAllowCustomSLTPTriggerPrice = mess.IsAllowCustomSLTPTriggerPrice;
        }
        if (!isNullOrUndefined(mess.IsAllowExerciseForItmOption)) {
            this.AllowExerciseForItmOption = mess.IsAllowExerciseForItmOption;
        }
        if (this.InstrType === InstrumentTypes.FORWARD) {
            if (mess.LastTradeDate) {
                this.LastTradeDate = mess.LastTradeDate;
            }
            if (mess.MaturityDate) {
                this.ExpDateReal = mess.MaturityDate;
            }
        }
        this.VolumeHistoryMode =
            mess.InstrType === InstrumentTypes.FOREX ||
                mess.InstrType === InstrumentTypes.EQUITIES_CFD ||
                mess.InstrType === InstrumentTypes.CFD_FUTURES ||
                mess.InstrType === InstrumentTypes.SPREADBET
                ? VolumeHistoryMode.Ticks
                : VolumeHistoryMode.Trades;
        this._isOptions = mess.IsOptions;
        this._underlierOptionInfo = mess.underlierOptionInfo;
        this.isAllowOTHTrading = mess.IsAllowOTHTrading;
    }
    UpdateHideRouteMode() {
        // TODO
        // let routesSupported = this.RoutesSupportedArray.slice(); //#43596
        // for (let j = 0; j < this.TradableRoutesArray.length; j++)
        // {
        //    if (routesSupported.Contains(this.TradableRoutesArray[j]))
        //        routesSupported.splice(routesSupported.indexOf(this.TradableRoutesArray[j]), 1);
        // }
        if (this.TradableRoutesArray.length > 1 || this.RoutesSupportedArray.length > 1) {
            this.isHideRouteMode = false;
        }
        else {
            this.isHideRouteMode = true;
        }
        this.UpdateHideRouteModeLangAliases();
    }
    static CloneLangAliases(alliases) {
        if (!alliases) {
            return;
        }
        const result = {};
        const keys = Object.keys(alliases);
        for (let i = 0; i < keys.length; i++) {
            result[keys[i]] = { Name: alliases[keys[i]].Name, Description: alliases[keys[i]].Description };
        }
        return result;
    }
    UpdateHideRouteModeLangAliases() {
        if (!this.FLanguageAliases) {
            return;
        }
        const keys = Object.keys(this.FLanguageAliases);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const isFullName = InstrumentUtils.IsFullName(this.FLanguageAliases[key].Name);
            if (isFullName && this.isHideRouteMode) {
                this.FLanguageAliases[key].Name = InstrumentUtils.RemoveRouteName(this.FLanguageAliases[key].Name);
            }
            else if (!isFullName && !this.isHideRouteMode) {
                this.FLanguageAliases[key].Name = InstrumentUtils.GetFullNameUnsafe(this.FLanguageAliases[key].Name, this.RouteName);
            }
        }
    }
    IsPositionCloseOnly(productType, account) {
        const riskSettings = this.RiskSettings;
        return (riskSettings.IsPositionCloseOnly(productType, account) ||
            account.AccountTradeStatus == AccountTradeStatus.LIQUIDATION_ONLY_STATUS ||
            this.TradingMode === TradingMode.LiquidationOnly); // #113662
    }
    getLotStep(productType, account) {
        var _a;
        return (_a = this.RiskSettings.getLotStep(productType, account)) !== null && _a !== void 0 ? _a : this.lotStep;
    }
    getMinLot(productType, account = undefined) {
        const riskSettings = this.RiskSettings;
        return riskSettings.getMinLot(productType, account) !== -1 // сперва смотрим есть ли в Risk Plan
            ? riskSettings.getMinLot(productType, account)
            : this.MinLot; // MinLot есть всегда т.к. отключить настройку Minimum lot на инструменте возможности нет
    }
    getMaxLot(productType, account = undefined) {
        const riskSettings = this.RiskSettings;
        return riskSettings.getMaxLot(productType, account) !== -1 // сперва смотрим есть ли в Risk Plan
            ? riskSettings.getMaxLot(productType, account)
            : this.MaxLot !== -1 // иначе смотрим есть ли в инструменте,
                ? this.MaxLot
                : NumericUtils.MAXVALUE; // если и там нет, то по такому инструменту ограничение не накладывается
    }
    getMaxPositionValue(productType, account = undefined) {
        const riskSettings = this.RiskSettings;
        return riskSettings.getMaxPositionValue(productType, account);
    }
    getPositionValueCurrencyId(productType, account = undefined) {
        const riskSettings = this.RiskSettings;
        return riskSettings.getPositionValueCurrencyId(productType, account);
    }
    getMaxLotInCash() {
        if (this.MaxLotInCash !== -1) {
            return this.MaxLotInCash;
        }
        else {
            return NumericUtils.MAXVALUE;
        }
    }
    getExp1(exp1, exp2) {
        this.HideAsset = true; // #88334
        // используем валюту из мессаджа (если exp1==exp2 - это сток нашего сервера)
        if (exp1 && exp1 !== exp2) {
            this.HideAsset = false;
            return exp1;
        }
        // используем имя инструмента
        if (!this.isForexSymbol()) {
            return this.ShortName;
        }
        // форексный - парсим и возврщаем CCY1
        const index = this.ShortName.indexOf('/');
        return index === -1 ? this.DataCache.baseCurrency : this.ShortName.substring(0, index);
    }
    getExp2(exp2) {
        // используем валюту из мессаджа
        if (exp2) {
            return exp2;
        }
        // нефорексный - используем базовую
        if (!this.isForexSymbol()) {
            return this.DataCache.baseCurrency;
        }
        // форексный - парсим и возврщаем CCY2
        const index = this.ShortName.indexOf('/');
        return index === -1 ? this.DataCache.baseCurrency : this.ShortName.substring(index + 1);
    }
    isForexSymbol() {
        return this.InstrType === InstrumentTypes.FOREX;
    }
    // TODO
    isFutureOrOption() {
        return this.InstrType === InstrumentTypes.FUTURES || this.InstrType === InstrumentTypes.OPTIONS;
    }
    toString() {
        return this.isHideRouteMode ? this.ShortName : this.DisplayFullName ? this.DisplayFullName : this.FullName;
    }
    toStringWithDescr() {
        const name = this.toString();
        let res = name;
        if (this.Description) {
            res = res + ' (' + this.Description + ')';
        }
        return res;
    }
    RouteIsTradable() {
        const route = this.DataCache.Routes.getRouteByName(this.Route);
        return route ? route.IsTradable : false;
    }
    getRoute() {
        return this.Route;
    }
    IsFakeNonFixed() {
        return this.Exp2 === Instrument.FAKE_NONFIXED_RESPONSE;
    }
    getTypeString() {
        return Instrument.getTypeString(this.InstrType, this.CFD);
    }
    // TODO. Refactor.
    static getTypeString(instrumentType, cfd) {
        return InstrumentUtils.getInstrumentTypeStringLocalized(instrumentType, cfd);
    }
    static getSupportedRoutes(supportedRoutes) {
        const resultArr = [];
        const splits = supportedRoutes.split(/,|;/);
        const len = splits.length;
        for (let i = 0; i < len; i++) {
            const split = splits[i];
            if (split)
                resultArr.push(split);
        }
        return resultArr;
    }
    truncatePriceToPrecision(price) {
        if (isNaN(price)) {
            price = 0;
        }
        const point = this.getPrecision(price);
        return MathUtils.TruncateDouble(price, point);
    }
    // TODO. Optimize. Instrument.format() NumberFormat creation + getPrecision().
    formatPrice(price, useVariableTickSize = true, withRounding = false) {
        return PriceFormatter.formatPrice(price, useVariableTickSize ? this.getPrecision(price) : this.Precision, withRounding);
    }
    getDoubleVolume(volume, showLots) {
        return showLots && !isNaN(volume) ? volume / this.getLotSize() : volume;
    }
    roundPrice(price) {
        return ticksUtils.RoundPriceToNearestPointSize(price, this.VariableTickList);
    }
    getAmountPrecision(inLotsPrefferedValue = null, account, productType = ProductType.General) {
        const inLots = inLotsPrefferedValue === null ? BaseSettings.isQuantityInLots : inLotsPrefferedValue;
        const isSpreadBetOrCryptoCCY = this.InstrType === InstrumentTypes.SPREADBET || this.InstrType === InstrumentTypes.CRYPTO;
        return MathUtils.getPrecision(this.getLotStep(productType, account) * (isSpreadBetOrCryptoCCY || inLots ? 1 : this.LotSize));
    }
    // TODO. Optimize. Use MathUtils.GetValuePrecision()?
    getPrecision(price = undefined) {
        if (!price) {
            return this.getDefaultPrecision();
        }
        if (this.VariableTickList.length > 1) {
            const tick = ticksUtils.FindVariableTick(price, this.VariableTickList);
            const tickSize = tick.PointSize;
            let prec = 0;
            let floor = Math.abs(Math.floor(tickSize) - tickSize);
            while (floor != 0) {
                floor *= 10;
                floor = Math.abs(Math.floor(floor) - floor);
                prec++;
            }
            return prec;
        }
        else {
            return this.getDefaultPrecision();
        }
    }
    getDefaultPrecision() {
        // TODO.
        // if (this.Precision >= 0)
        return this.Precision;
        // TODO.
        // return getInstrumentTypeSttings().DecimalPlaces;
    }
    getLotStepPrecision(productType, account) {
        const cacheKey = `${productType !== null && productType !== void 0 ? productType : 'null'}:${account != null ? account.AcctNumber : 'null'}`;
        if (!this.lotStepPrecisionCache.has(cacheKey)) {
            const lotStep = this.getLotStep(productType, account);
            const precision = MathUtils.getPrecision(lotStep);
            this.lotStepPrecisionCache.set(cacheKey, precision);
        }
        return this.lotStepPrecisionCache.get(cacheKey);
    }
    CreateVariableTick(arrayOfVariableTickObjects) {
        const res = [];
        const len = arrayOfVariableTickObjects.length;
        for (let i = 0; i < len; i++) {
            res.push(new VariableTick(arrayOfVariableTickObjects[i]));
        }
        return res;
    }
    // #region VariableTickSize
    MinimumPointSize() {
        // Тополь сказал, что первый всегда минимальный
        return this.VariableTickList[0].PointSize;
    }
    MinimumPipsSize(ignoreFractionalTicks = false) {
        const isAllowFractionalTick = !ignoreFractionalTicks && ticksUtils.AllowUseFractinalTicksForForex(this.InstrType);
        return ticksUtils.GetPipsSize(this.MinimumPointSize(), isAllowFractionalTick, BaseSettings.offsetType);
    }
    get ExchangeSessionName() {
        return this.exchangeSessionName;
    }
    set ExchangeSessionName(value) {
        this.exchangeSessionName = value;
        // захаркодили для кувейта
        this.Level1.CbAuctionStart = this.exchangeSessionName === 'CB Auction' ? DateTimeUtils.DateTimeUtcNow() : null;
        this.ExchangeSessionNameUpdated.Raise();
    }
    get PipsSize() {
        if (this.pipsSizeSpecified !== null) {
            return this.pipsSizeSpecified;
        }
        return ticksUtils.GetPipsSize(this.PointSize, ticksUtils.AllowUseFractinalTicksForForex(this.InstrType), BaseSettings.offsetType);
    }
    calculatePrice(price, ticks, ignoreFractionalTicks = false) {
        const isFractionalTicksAllowed = !ignoreFractionalTicks && ticksUtils.AllowUseFractinalTicksForForex(this.InstrType);
        return ticksUtils.CalculatePrice(price, ticks, this.VariableTickList, isFractionalTicksAllowed, BaseSettings.offsetType);
    }
    calculateTicks(price, offset, ignoreFractionalTicks = false) {
        const isFractionalTicksAllowed = !ignoreFractionalTicks && ticksUtils.AllowUseFractinalTicksForForex(this.InstrType);
        return ticksUtils.CalculateTicks(price, offset, this.VariableTickList, isFractionalTicksAllowed, BaseSettings.offsetType);
    }
    // #endregion VariableTickSize
    GetPointSize(price = null) {
        // pass price to get PointSize for variable ticksize ins
        if (!isValidArray(this.VariableTickList)) {
            return this.PointSize;
        }
        // for First PointSize Case
        if (price === null && this.VariableTickList[0]) {
            return this.VariableTickList[0].PointSize;
        }
        const tick = ticksUtils.FindVariableTick(price, this.VariableTickList);
        return tick != null ? tick.PointSize : this.PointSize;
    }
    getCompanyName() {
        let data = '';
        if (this.Description != null && this.Description.length > 0) {
            data = this.Description;
        }
        return data;
    }
    UpdateHigh(high, curPrice) {
        if (curPrice > high || isNaN(high)) {
            return curPrice;
        }
        return high;
    }
    UpdateLow(low, curPrice) {
        if (curPrice < low || isNaN(low)) {
            return curPrice;
        }
        return low;
    }
    roundProfit(sum) {
        if (this.asset_exp2 !== null) {
            const d = Math.pow(10, this.asset_exp2.Point);
            return Math.round(sum * d) / d;
        }
        if (this.PointSize >= 0.01) {
            const p = 1 / this.PointSize;
            return Math.round(sum * p) / p;
        }
        else {
            return Math.round(sum);
        }
    }
    // #region NewQuote. TODO.
    newQuote(msg) {
        this.OHLCCalculator(msg);
        this.Level1.NewQuote(msg);
        this.InstrumentDayInfo.NewQuote(msg);
        const dsInsArr = this.DataCache.GetInstrumentsByDataSource(this);
        for (let i = 0; i < dsInsArr.length; i++) {
            dsInsArr[i].DataSourceLevel1.NewQuote(msg);
        }
        this.DataSourceLevel1.NewQuote(msg);
        switch (msg.Type) {
            case HistoryType.QUOTE_LEVEL1:
                this.processQuote1Message(msg);
                break;
            case HistoryType.QUOTE_INSTRUMENT_DAY_BAR:
                this.processInstrumentDayBarMessage(msg);
                break;
            case HistoryType.QUOTE_TRADES:
                this.processQuoteTradeMessage(msg);
                break;
        }
    }
    processQuote1Message(quote1Msg) {
        /* TODO. */
        if (this.InstrumentDayBarMessageUpdateMode) {
            this.SetLastQuote1Message(quote1Msg);
            return;
        }
        const currentSession = /* TODO. */ this.Session.CurrentSessionPeriod;
        let updateBidLow = false;
        let updateAskLow = false; // #42918
        let updateBidHigh = false;
        let updateAskHigh = false; // #42918
        if (quote1Msg.Open !== null) {
            this.BidOpen = quote1Msg.Open;
        }
        // <0
        if (isNaN(this.BidOpen) && this.HistoryType !== HistoryType.QUOTE_TRADES) {
            this.BidOpen = quote1Msg.Bid;
        }
        if (quote1Msg.High !== null) {
            this.BidHigh = quote1Msg.High;
        }
        else {
            this.BidHigh = this.UpdateHigh(this.BidHigh, quote1Msg.Bid);
            if (this.BidHigh === quote1Msg.Bid) {
                updateBidHigh = true;
            }
        }
        if (quote1Msg.Low !== null) {
            this.BidLow = quote1Msg.Low;
        }
        else {
            this.BidLow = this.UpdateLow(this.BidLow, quote1Msg.Bid);
            if (this.BidLow === quote1Msg.Bid) {
                updateBidLow = true;
            }
        }
        if (quote1Msg.PrevClose !== null) {
            this.BidPrevClose = quote1Msg.PrevClose;
        }
        if (quote1Msg.AskOpen !== null) {
            this.AskOpen = quote1Msg.AskOpen;
        }
        // < 0
        if (isNaN(this.AskOpen) && this.HistoryType !== HistoryType.QUOTE_TRADES) {
            this.AskOpen = quote1Msg.Ask;
        }
        if (quote1Msg.AskHigh !== null) {
            this.AskHigh = quote1Msg.AskHigh;
        }
        else {
            this.AskHigh = this.UpdateHigh(this.AskHigh, quote1Msg.Ask);
            if (this.AskHigh === quote1Msg.Ask) {
                updateAskHigh = true;
            }
        }
        if (quote1Msg.AskLow !== null) {
            this.AskLow = quote1Msg.AskLow;
        }
        else {
            this.AskLow = this.UpdateLow(this.AskLow, quote1Msg.Ask);
            if (this.AskLow === quote1Msg.Ask) {
                updateAskLow = true;
            }
        }
        if (quote1Msg.AskPrevClose !== null) {
            this.AskPrevClose = quote1Msg.AskPrevClose;
        }
        if (isNaN(this.AskOpen)) {
            this.AskOpen = quote1Msg.Ask;
        }
        if (isNaN(this.BidOpen)) {
            this.BidOpen = quote1Msg.Bid;
        }
        if (quote1Msg.Ticks !== null) {
            this.Ticks = quote1Msg.Ticks;
        }
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES // &&
        // currentSession //&&
        /* currentSession.DayPeriod === DayPeriods.MAIN */
        ) {
            const hType = this.HistoryType;
            if ((hType === HistoryType.QUOTE_ASK && !isNaN(quote1Msg.Ask)) || // Ask price
                (hType === HistoryType.QUOTE_LEVEL1 && !isNaN(quote1Msg.Bid))) // Bid price
             {
                this.Ticks++;
            }
        }
        if (quote1Msg.TicksPreMarket !== null) {
            this.TicksPreMarket = quote1Msg.TicksPreMarket;
        }
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES &&
            currentSession &&
            currentSession.dayPeriod === DayPeriods.PRE_OPEN) {
            this.TicksPreMarket++;
        }
        if (quote1Msg.TicksPostMarket !== null) {
            this.TicksPostMarket = quote1Msg.TicksPostMarket;
        }
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES &&
            currentSession &&
            currentSession.dayPeriod === DayPeriods.POST_CLOSE) {
            this.TicksPostMarket++;
        }
        if (quote1Msg.VolumePreMarket !== null) {
            this.VolumePreMarket = quote1Msg.VolumePreMarket;
        }
        if (quote1Msg.VolumePostMarket !== null) {
            this.VolumePostMarket = quote1Msg.VolumePostMarket;
        }
        if (quote1Msg.PrevSettlementPrice !== null) {
            this.PrevSettlementPrice = quote1Msg.PrevSettlementPrice;
        }
        if (quote1Msg.BidMainClose !== null) {
            this.BidMainClose = quote1Msg.BidMainClose;
        }
        if (quote1Msg.AskMainClose !== null) {
            this.AskMainClose = quote1Msg.AskMainClose;
        }
        // есть у нас биды и аски. Выбираем, что показывать.
        // для аска -аски, для трейдов - ничего (левел3 обновит), для остальных - биды
        if (this.HistoryType === HistoryType.QUOTE_ASK) {
            // #48594 - сменилась сессия, Open -  был сброшен в -1 в Idpm // теперь в NaN а не -1
            if (quote1Msg.AskOpen !== null || isNaN(this.Open)) {
                this.Open = this.AskOpen;
            }
            if (quote1Msg.AskHigh !== null || updateAskHigh) {
                this.High = this.AskHigh;
            }
            if (quote1Msg.AskLow !== null || updateAskLow) {
                this.Low = this.AskLow;
            }
            if (quote1Msg.AskPrevClose !== null) {
                this.Close = this.AskPrevClose;
            }
        }
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES) {
            if (quote1Msg.Open !== null /* || this.Open < 0 */ || isNaN(this.Open)) {
                this.Open = this.BidOpen;
            }
            if (quote1Msg.High !== null || updateBidHigh) {
                this.High = this.BidHigh;
            }
            if (quote1Msg.Low !== null || updateBidLow) {
                this.Low = this.BidLow;
            }
            if (quote1Msg.PrevClose !== null) {
                this.Close = this.BidPrevClose;
            }
        }
        else {
            // TODO EBY KAK TYT
            if (!isNaN(this.BidOpen)) {
                this.Open = this.BidOpen;
            }
            if (!isNaN(this.BidPrevClose)) {
                this.Close = this.BidPrevClose;
            }
            // их нельзя переопределять л1, их переопределит трейдмессадж (см. ниже). Доверяю только оригинальному, присланному в мессадже
            if (quote1Msg.High !== null) {
                this.High = this.BidHigh;
            }
            if (quote1Msg.Low !== null) {
                this.Low = this.BidLow;
            }
        }
        // если сервер прислал вольюмТотал, я обновляю мой теущий суммарный вольюм.
        if (quote1Msg.volumeTotal !== null) {
            this.Volume = quote1Msg.volumeTotal;
        }
        // если вольюм не приходит, инкрементим вольюм, но только не для трейда, для трейда инкремент только по квот3 мессаджу из сайза
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES && quote1Msg.QuoteAdditionalInfo) {
            this.Volume += quote1Msg.QuoteAdditionalInfo.AccumulatedVolume;
        }
        // если сервер прислал вольюмТотал, я обновляю мой теущий суммарный вольюм.
        if (quote1Msg.TotalVolume !== null) {
            this.TotalVolume = quote1Msg.TotalVolume;
        }
        else if (this.HistoryType !== HistoryType.QUOTE_TRADES && quote1Msg.QuoteAdditionalInfo) {
            this.TotalVolume += quote1Msg.QuoteAdditionalInfo.AccumulatedVolume;
        }
        this.SetLastQuote1Message(quote1Msg);
    }
    processInstrumentDayBarMessage(msg) {
        // Новая схема, когда приходит несколько InstrumentDayBarMessage'й
        // если инструмент строится по трейдам,
        // то мы берем только значения из мессаджа
        // с InstrumentBarType == QuoteMessage.QUOTE_TRADES
        // если не по трейдам то мы берем значения из мессаджей с асками и бидами,
        // чтобы их можно было получить потом спредированные значения
        if ((msg.InstrumentBarType === HistoryType.QUOTE_TRADES && this.HistoryType === HistoryType.QUOTE_TRADES) ||
            (msg.InstrumentBarType === HistoryType.QUOTE_LEVEL1 && this.HistoryType !== HistoryType.QUOTE_TRADES)) {
            if (msg.Open !== null) {
                this.BidOpen = msg.Open;
            }
            if (msg.High !== null) {
                this.BidHigh = msg.High;
            }
            if (msg.Low !== null) {
                this.BidLow = msg.Low;
            }
            if (msg.PreviousClosePrice !== null) {
                this.BidPrevClose = msg.PreviousClosePrice;
            }
            if (msg.TodayClosePrice !== null) {
                this.BidMainClose = msg.TodayClosePrice;
            }
        }
        // для всех остальных типов инструментов мы берем значения из месаджей
        // где приходят биды и аски, чтобы можно  было их спредировать
        else if (msg.InstrumentBarType === HistoryType.QUOTE_ASK && this.HistoryType !== HistoryType.QUOTE_TRADES) {
            if (msg.Open !== null) {
                this.AskOpen = msg.Open;
            }
            if (msg.High !== null) {
                this.AskHigh = msg.High;
            }
            if (msg.Low !== null) {
                this.AskLow = msg.Low;
            }
            if (msg.PreviousClosePrice !== null) {
                this.AskPrevClose = msg.PreviousClosePrice;
            }
            if (msg.TodayClosePrice !== null) {
                this.AskMainClose = msg.TodayClosePrice;
            }
        }
        if (msg.InstrumentBarType === HistoryType.QUOTE_ASK && this.HistoryType === HistoryType.QUOTE_ASK) {
            if (msg.Open !== null) {
                this.Open = this.AskOpen;
            }
            if (msg.High !== null) {
                this.High = this.AskHigh;
            }
            if (msg.Low !== null) {
                this.Low = this.AskLow;
            }
            if (msg.PreviousClosePrice !== null) {
                this.Close = this.AskPrevClose;
            }
        }
        else {
            if (msg.Open !== null) {
                this.Open = this.BidOpen;
            }
            if (msg.High !== null) {
                this.High = this.BidHigh;
            }
            if (msg.Low !== null) {
                this.Low = this.BidLow;
            }
            if (msg.PreviousClosePrice !== null) {
                this.Close = this.BidPrevClose;
            }
        }
        if (msg.InstrumentBarType === this.HistoryType) {
            if (msg.Ticks !== null) {
                this.Ticks = msg.Ticks;
            }
            if (msg.Volume !== null && !isNaN(msg.Volume)) {
                this.TotalVolume = msg.Volume;
            }
            if (msg.OpenInterest !== null) {
                this.OpenInterest = msg.OpenInterest;
            }
            if (msg.SettlementPrice !== null) {
                this.SettlementPrice = msg.SettlementPrice;
            }
            if (msg.PrevSettlementPrice !== null) {
                this.PrevSettlementPrice = msg.PrevSettlementPrice;
            }
            if (msg.FiftyTwoWeekHighPrice !== null) {
                this.FiftyTwoWeekHighPrice = msg.FiftyTwoWeekHighPrice;
            }
            if (msg.FiftyTwoWeekLowPrice !== null) {
                this.FiftyTwoWeekLowPrice = msg.FiftyTwoWeekLowPrice;
            }
            if (msg.High13Week !== null) {
                this.High13Week = msg.High13Week;
            }
            if (msg.Low13Week !== null) {
                this.Low13Week = msg.Low13Week;
            }
            if (msg.High26Week !== null) {
                this.High26Week = msg.High26Week;
            }
            if (msg.Low26Week !== null) {
                this.Low26Week = msg.Low26Week;
            }
        }
        if (msg.Delay !== null) {
            this.QuoteDelay.SetDelay(msg.Delay, msg.Type);
        }
    }
    processQuoteTradeMessage(message) {
        // #61359 обновление Opent Interest приоритетнее,
        // чем флаг instrumentDayBarMessageUpdateMode
        if (message.OpenInterest !== null) {
            this.OpenInterest = message.OpenInterest;
        }
        if (this.InstrumentDayBarMessageUpdateMode) {
            return;
        }
        if (this.HistoryType !== HistoryType.QUOTE_TRADES) {
            return;
        }
        // let currentSession = Instrument.FindSession(this, message.cTime);
        // TODO. Ugly. Optimization?
        const currentSession = message.TradingSession;
        // всегда увеличиваю объем, но только для трейдов.
        if (this.DataCache.IgnoreTradeSession // ||
        /* currentSession && currentSession.DayPeriod === DayPeriods.MAIN */
        ) {
            this.Ticks++;
            this.Volume += message.Size;
        }
        else if (currentSession && currentSession.DayPeriod === DayPeriods.PRE_OPEN) {
            this.TicksPreMarket++;
            this.VolumePreMarket += message.Size;
        }
        else if (currentSession && currentSession.DayPeriod === DayPeriods.POST_CLOSE) {
            this.TicksPostMarket++;
            this.VolumePostMarket += message.Size;
        }
        // #55011: Сервер прислыает изначально NaN, чтобы клиент локализировал в N/A,
        // но по факту счетчик на клиенте должен начинатся с 0.
        // Если сейчас totalVolume = 0, то объемом будет объем из котировки
        if (isNaN(this.TotalVolume)) {
            this.TotalVolume = message.Size;
        }
        else {
            this.TotalVolume += message.Size;
        }
        this.High = this.UpdateHigh(this.High, message.Price);
        this.Low = this.UpdateLow(this.Low, message.Price);
        if (isNaN(this.Open)) {
            this.Open = message.Price;
        }
        // }
    }
    SetLastQuote1Message(msg) {
        if (msg.IsValid) {
            if (!this.FirstQuote) {
                this.FirstQuote = msg;
            }
            this.LastQuote = msg;
            this.LastInvalidQuote = null;
        }
        else {
            this.LastInvalidQuote = msg;
            this.NewQuote.Raise(msg);
        }
    }
    GetLastQuote(valid) {
        switch (valid) {
            case QuoteValid.Last:
                return this.LastInvalidQuote || this.LastQuote;
            case QuoteValid.Valid:
                return this.LastQuote;
            case QuoteValid.Invalid:
                return this.LastInvalidQuote;
        }
        return null;
    }
    OHLCCalculator(message) {
        if (message.Type === HistoryType.QUOTE_LEVEL1) {
            if (this.InstrumentDayBarMessageUpdateMode) {
                return;
            }
            if (message.High != null) {
                this.OHLC_BidHigh = message.High;
            }
            else {
                this.OHLC_BidHigh = this.UpdateHigh(this.OHLC_BidHigh, message.Bid);
            }
            if (message.Low != null) {
                this.OHLC_BidLow = message.Low;
            }
            else {
                this.OHLC_BidLow = this.UpdateLow(this.OHLC_BidLow, message.Bid);
            }
            if (message.AskHigh != null) {
                this.OHLC_AskHigh = message.AskHigh;
            }
            else {
                this.OHLC_AskHigh = this.UpdateHigh(this.OHLC_AskHigh, message.Ask);
            }
            if (message.AskLow != null) {
                this.OHLC_AskLow = message.AskLow;
            }
            else {
                this.OHLC_AskLow = this.UpdateLow(this.OHLC_AskLow, message.Ask);
            }
        }
        else if (message.Type === HistoryType.QUOTE_TRADES) {
            if (this.InstrumentDayBarMessageUpdateMode) {
                return;
            }
            this.OHLC_TradeHigh = this.UpdateHigh(this.OHLC_TradeHigh, message.Price);
            this.OHLC_TradeLow = this.UpdateLow(this.OHLC_TradeLow, message.Price);
        }
        else if (message.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) // #41123
         {
            // #region Cтарая схема, когда приходит один InstrumentDayBarMessage
            // обновляем данные только если тип заданный в мессадже совпадает с типом инструмента (или вообще не пришел)
            if (!message.InstrumentBarType /* || (int)message.InstrumentBarType == FHistoryType */) {
                if (message.High != null) {
                    this.OHLC_TradeHigh = this.OHLC_BidHigh = message.High;
                }
                if (message.Low != null) {
                    this.OHLC_TradeLow = this.OHLC_BidLow = message.Low;
                }
            }
            // #endregion Cтарая схема, когда приходит один InstrumentDayBarMessage
            // #region Новая схема, когда приходит несколько InstrumentDayBarMessage'й
            else // message.InstrumentBarType.HasValue
             {
                if (message.InstrumentBarType == HistoryType.QUOTE_TRADES) {
                    if (message.High != null) {
                        this.OHLC_TradeHigh = message.High;
                    }
                    if (message.Low != null) {
                        this.OHLC_TradeLow = message.Low;
                    }
                }
                else if (message.InstrumentBarType == HistoryType.QUOTE_ASK) {
                    if (message.High != null) {
                        this.OHLC_AskHigh = message.High;
                    }
                    if (message.Low != null) {
                        this.OHLC_AskLow = message.Low;
                    }
                }
                else // bid
                 {
                    if (message.High != null) {
                        this.OHLC_BidHigh = message.High;
                    }
                    if (message.Low != null) {
                        this.OHLC_BidLow = message.Low;
                    }
                }
            }
        }
    }
    GetHigh(type) {
        switch (type) {
            case HistoryType.ASK:
                return this.OHLC_AskHigh;
            case HistoryType.LAST:
                return this.OHLC_TradeHigh;
            case HistoryType.BID:
            default:
                return this.OHLC_BidHigh;
        }
    }
    GetLow(type) {
        switch (type) {
            case HistoryType.ASK:
                return this.OHLC_AskLow;
            case HistoryType.LAST:
                return this.OHLC_TradeLow;
            case HistoryType.BID:
            default:
                return this.OHLC_BidLow;
        }
    }
    GetInterTraderButtonStatus(forTradeButton) {
        // #87891
        const orderType = forTradeButton ? OrderType.Market : OrderType.Limit;
        const tradingStatus = this.TradingMode;
        const fullyOpen = tradingStatus === TradingMode.FullyOpen;
        const fullyClosed = tradingStatus === TradingMode.FullyClosed || tradingStatus === TradingMode.CAMaintenance;
        const tradingHalt = tradingStatus === TradingMode.TradingHalt;
        const allowedBySession = this.Check_Session_OrderType(orderType);
        const result = {
            status: InterTraderBtnStatus.Open,
            tooltip: forTradeButton ? 'panel.watchlist.Trade.tt' : 'panel.watchlist.Order.tt'
        };
        if (fullyOpen && allowedBySession) {
            return result;
        }
        if ((fullyOpen && !allowedBySession) || this.Session.BlockTrading || fullyClosed) {
            result.status = InterTraderBtnStatus.Closed;
            result.tooltip = 'InstrumentDetailsPanel.Closed';
            return result;
        }
        if (tradingHalt) {
            // let quote = this.LastQuote                                                                        //закомментировал в связи с https://tp.traderevolution.com/entity/88592
            // let minutesForCallStatus = this.InstrumentAdditionalInfo['Time for Call status']
            const callNumber = this.InstrumentAdditionalInfo['Call status_telephone number'];
            result.status = InterTraderBtnStatus.Call;
            result.tooltip = callNumber;
            // if (quote && minutesForCallStatus !== undefined)                                                 //закомментировал в связи с https://tp.traderevolution.com/entity/88592
            // {
            //     let minutesAfterLastQuote = (Date.now() - quote.Time.getTime()) / TimeSpan.TicksPerMinute
            //     if (minutesAfterLastQuote > parseInt(minutesForCallStatus))
            //     {
            //         result.status = InterTraderBtnStatus.Call
            //         result.tooltip = callNumber
            //         return result
            //     }
            // }
        }
        return result;
    }
    // #endregion
    getLotSize() {
        return this.LotSize;
    }
    isProductTypeVisible(account) {
        const riskPlanSettings = this.getRiskPlanSettingsForAccount(account);
        if (riskPlanSettings.availableProductTypes.length > 0) {
            return !riskPlanSettings.cache.hasOwnProperty(ProductType.General);
        }
        else {
            return false;
        }
    }
    getRiskPlanSettingsForAccount(account) {
        let result = this.RiskSettings.RiskPlanSettings;
        if (!(account === null || account === void 0 ? void 0 : account.RiskPlan)) {
            return result;
        }
        result = account.RiskPlan.GetRisksForInstrument(this.GetInteriorID()) || result;
        return result;
    }
    getRiskPlanItemForAccountAndProductType(account, productType) {
        const riskPlanSettings = this.getRiskPlanSettingsForAccount(account);
        if (!isNullOrUndefined(riskPlanSettings)) {
            return riskPlanSettings.get(productType);
        }
        return null;
    }
    isHedgingNettingType() {
        return this.TradingPositionType === TradingPositionType.MultiPosition;
    }
    isMultiPositionPerSide() {
        return this.TradingPositionType === TradingPositionType.MultiPositionPerSide;
    }
    isLeverageVisible(account, productType) {
        const riskPlanItem = this.getRiskPlanItemForAccountAndProductType(account, productType);
        const isHedgingNettingType = this.isHedgingNettingType();
        const visible = riskPlanItem ? riskPlanItem.IsLeverageVisible() && isHedgingNettingType : false;
        return visible;
    }
    getLeverages(account, productType) {
        const riskPlanItem = this.getRiskPlanItemForAccountAndProductType(account, productType);
        const result = !isNullOrUndefined(riskPlanItem) ? riskPlanItem.GetLeverages() : [];
        return result;
    }
    // TODO. Refactor. Optimize.
    // formatPrice с большей точностью(+2)
    formatPricePrecision(price, withRounding = false, useVariableTickSize = true) {
        if (!isFinite(price) || isNaN(price)) {
            price = 0;
        }
        // если настройка включена используем стандартный вывод
        if (BaseSettings.roundedAverageOpenPrice) {
            return this.formatPrice(price, useVariableTickSize, withRounding);
        }
        else {
            return PriceFormatter.formatPrice(price, InstrumentUtils.MAX_PRECISION_ON_SERVER, withRounding);
        }
        // let precInstr = this.getPrecision()
        // let precNeed = precInstr
        // // #46163 - отображаем цену с такой же точностью как и на сервере
        // for (let i = precInstr; i < Instrument.MAX_PRECISION_ON_SERVER; i++)
        // {
        //     let dVal = price * Math.pow(10, precNeed) + MathUtils.MATH_ROUND_EPSILON
        //     let fVal = price * Math.pow(10, Instrument.MAX_PRECISION_ON_SERVER) + MathUtils.MATH_ROUND_EPSILON
        //     fVal = Math.round(fVal) / Math.pow(10, Instrument.MAX_PRECISION_ON_SERVER - precNeed)
        //     if (fVal - Math.trunc(dVal) > 0)
        //         precNeed++
        //     else
        //         break
        // }
        // return Instrument.formatPrice(price, precNeed)
    }
    GetTradeSessionStatusId() {
        let tradeSessionStatusId = this.TradeSessionStatusId;
        const forwardBaseInstrument = this.ForwardBaseInstrument;
        if (tradeSessionStatusId === -1 && forwardBaseInstrument) {
            tradeSessionStatusId = forwardBaseInstrument.TradeSessionStatusId;
        }
        return tradeSessionStatusId;
    }
    AllowFilterBarsBySession(period) {
        if (period < Periods.MIN || period >= Periods.DAY) {
            return false;
        }
        return this.UseSessionPeriodForIntradayChart || false;
    }
    static GetAllowedOperations(instrument) {
        let allowedOperations = SessionOperations.None;
        const tradeSessionStatusId = instrument.GetTradeSessionStatusId();
        if (tradeSessionStatusId !== -1) {
            const tradeSessionStatus = instrument.DataCache.GetTradeSessionStatus(tradeSessionStatusId);
            if (tradeSessionStatus) {
                allowedOperations = tradeSessionStatus.AllowedOperations;
            }
        }
        return allowedOperations;
    }
    static IsDecreaseOnlyPositionCount(instrument) {
        let decreaseOnlyPositionCount = false;
        const tradeSessionStatusId = instrument.GetTradeSessionStatusId();
        if (tradeSessionStatusId !== -1) {
            const tradeSessionStatus = instrument.DataCache.GetTradeSessionStatus(tradeSessionStatusId);
            if (tradeSessionStatus) {
                decreaseOnlyPositionCount = tradeSessionStatus.IsDecreaseOnlyPositionCount;
            }
        }
        else {
            const tradingSession = instrument.Session.CurrentSessionPeriod;
            if (tradingSession) {
                decreaseOnlyPositionCount = tradingSession.decreaseOnlyPositionCount;
            }
        }
        return decreaseOnlyPositionCount;
    }
    static CalculatePrecision(pointSize, variableTickList) {
        var _a;
        return ((_a = MathUtils.GetDecimalPlaces((variableTickList === null || variableTickList === void 0 ? void 0 : variableTickList.length) ? variableTickList[0].PointSize : pointSize, 20)) !== null && _a !== void 0 ? _a : 0);
    }
    CreateInstrumentMessage() {
        // if (_message != null)
        //    return (InstrumentMessage)_message.CreateCopy(); //так делать нельзя, но также нельзя отдавать ссылку, иначе по сслыке измениться закешированное значение
        var _a;
        const mess = new DirectInstrumentMessage();
        mess.Exp1 = this.Exp1;
        mess.Exp2 = this.Exp2;
        mess.AssetName = this.AssetName;
        mess.Id = this.Id;
        mess.Descr = this.Description;
        mess.UnderlierDescription = this.UnderlierDescription;
        mess.RoutesSupported = this.RoutesSupported;
        mess.Name = InstrumentUtils.RemoveRouteName(this.FullName);
        mess.LotSize = this.LotSize;
        mess.ContractSize = this.ContractSize;
        mess.TypeId = this.TypeId;
        mess.PointSize = this.PointSize;
        mess.Precision = (_a = this.Precision) !== null && _a !== void 0 ? _a : -1;
        mess.PipsSizeSpecified = this.pipsSizeSpecified;
        // TODO типа
        // RiskSettings.InitMessage(mess);
        mess.ShortSwapInfo = this.ShortSwapInfo;
        mess.SwapBuy = this.SwapBuy;
        mess.SwapSell = this.SwapSell;
        mess.TradingMode = this.TradingMode;
        mess.TradingPositionType = this.TradingPositionType;
        mess.HistoryType = this.HistoryType;
        mess.DefaultChartHistoryType = this.DefaultChartHistoryType;
        mess.TradableRoutes = this.TradableRoutes;
        mess.SwapType = this.SwapType;
        mess.InstrDateSettingsList = this.InstrDateSettingsList;
        mess.LotStep = this.lotStep;
        mess.FuturesTickCoast = this.FuturesTickCoast;
        mess.DeliveryMethod = this.DeliveryMethod;
        mess.TradingBalance = this.TradingBalance;
        mess.ExchangeId = this.ExchangeId;
        mess.TradingExchange = this.TradingExchange;
        mess.MarketDataExchange = this.MarketDataExchange;
        mess.CountryId = this.CountryId;
        mess.HightLimit = this.HightLimit;
        mess.LowLimit = this.LowLimit;
        mess.IsHighLimitFrontEndValidationEnabled = this.IsHighLimitFrontEndValidationEnabled;
        mess.IsLowLimitFrontEndValidationEnabled = this.IsLowLimitFrontEndValidationEnabled;
        mess.PriceLimitMeasure = this.PriceLimitMeasure;
        mess.SeriesGroupName = this.SeriesGroupName;
        mess.MinimalLot = this.MinLot;
        mess.MaxLot = this.MaxLot;
        mess.MaxLotInCash = this.MaxLotInCash;
        mess.VariableTickList = this.VariableTickList;
        mess.AdditionalFields = this.AdditionalFields;
        mess.InstrType = this.InstrType;
        mess.LockedBy = this.Locked;
        mess.TradingSessionsId = this.TradingSessionsId;
        mess.ContractMultiplier = this.ContractMultiplier;
        mess.ExerciseStyle = this.ExerciseStyle;
        mess.OptionTradingStyle = this.OptionTradingStyle;
        mess.InstrumentAdditionalInfo = this.InstrumentAdditionalInfo; // hsa: копировать не по ссылке? используем для сохранения в БД Алго
        mess.VolumeHistoryMode = this.VolumeHistoryMode;
        mess.QuotingType = this.QuotingType;
        mess.TradeSessionStatusId = this.TradeSessionStatusId;
        mess.ExchangeSessionName = this.ExchangeSessionName;
        // #region FixedIncome
        mess.LastTradeDate = this.LastTradeDate;
        mess.MaturityDate = this.MaturityDate;
        mess.FaceValue = this.FaceValue;
        mess.CouponRate = this.CouponRate;
        mess.CouponCycle = this.CouponCycle;
        mess.YieldRate = this.YieldRate;
        mess.AccruedInterest = this.AccruedInterest;
        mess.IssueDate = this.IssueDate;
        mess.DaysPerMonth = this.DaysPerMonth;
        mess.DaysPerYear = this.DaysPerYear;
        mess.InstrumentTradableID = this.InstrumentTradableID;
        mess.curPriceSource = this.CurPriceSource;
        if (this.UnderlierDescription || this.ForwardBaseInstrument) {
            mess.ForwardBaseInstrument = this.UnderlierDescription || this.ForwardBaseInstrument.UnderlierDescription;
        }
        if (this.Limits) {
            mess.HightLimit = this.Limits.HightLimit;
            mess.LowLimit = this.Limits.LowLimit;
            mess.PriceLimitMeasure = this.Limits.PriceLimitMeasure;
            mess.IsHighLimitFrontEndValidationEnabled = this.Limits.IsHighLimitFrontEndValidationEnabled;
            mess.IsLowLimitFrontEndValidationEnabled = this.Limits.IsLowLimitFrontEndValidationEnabled;
        }
        if (this.FLanguageAliases !== null) {
            mess.LanguageAliases = Instrument.CloneLangAliases(this.FLanguageAliases);
        }
        mess.IsOptions = this._isOptions;
        mess.underlierOptionInfo = this._underlierOptionInfo;
        mess.IsAllowOTHTrading = this.isAllowOTHTrading;
        return mess;
    }
    IsNoneMarginType(account, productType) {
        const riskPlanItem = this.getRiskPlanItemForAccountAndProductType(account, productType);
        return riskPlanItem.IsNoneMarginType();
    }
    IsAllowShortPositions(productType) {
        // #91969 - документация
        if (!this.RiskSettings.IsShortable(productType)) {
            return false;
        }
        if (this.DailyAvailableForShort === null || isNaN(this.DailyAvailableForShort)) {
            return true;
        }
        return this.DailyAvailableForShort > 0;
    }
    CalcPriceOnTrade() {
        if (this.CurPriceSource === CurPriceSource.Level1) {
            // всегда л1
            return false;
        }
        if (this.CurPriceSource === CurPriceSource.Trades) {
            // всегда трейд
            return true;
        }
        if (this.CurPriceSource === CurPriceSource.Level1MainOnly) {
            const curPeriod = this.Session.CurrentSessionPeriod; // com.pfsoft.proftrading.cache.Instrument.FindSession(instrument, quoteMessage.cTime);
            return curPeriod === null || !curPeriod.IsMain();
        }
        return false;
    }
    // TODO. Optimize allowed trading session orders.
    IsAllowOrderType(orderType) {
        const allowedOrderTypes = Instrument.GetAllowedOrderTypes(this);
        return allowedOrderTypes.includes(orderType);
    }
    static GetAllowedOrderTypes(instrument) {
        let result = [];
        const tradeSessionStatusId = instrument.GetTradeSessionStatusId();
        if (tradeSessionStatusId !== -1) {
            const sessionStatus = instrument.DataCache.GetTradeSessionStatus(tradeSessionStatusId);
            if (!isNullOrUndefined(sessionStatus)) {
                result = sessionStatus.AllowedOrderTypes;
            }
        }
        return result;
    }
    Check_Session_OrderType(lOrderType) {
        const allowedOrderTypes = Instrument.GetAllowedOrderTypes(this);
        return (allowedOrderTypes.Contains(lOrderType) ||
            (lOrderType == OrderType.TrailingStop &&
                this.DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_DONT_USE_ROUTE_SETTING_FOR_CLOSE_TS_ORDER)) ||
            (lOrderType == OrderType.OCO &&
                allowedOrderTypes.Contains(OrderType.Limit) &&
                allowedOrderTypes.Contains(OrderType[BaseSettings.isUseStopLimitInsteadStop() ? 'StopLimit' : 'Stop'])));
    }
    IsAllowTif(tif) {
        const allowedTifs = this.GetAllowedTifs();
        return allowedTifs.includes(tif);
    }
    GetAllowedTifs() {
        let result = [];
        const tradeSessionStatusId = this.GetTradeSessionStatusId();
        if (tradeSessionStatusId !== -1) {
            const sessionStatus = this.DataCache.GetTradeSessionStatus(tradeSessionStatusId);
            if (!isNullOrUndefined(sessionStatus)) {
                result = sessionStatus.AllowedTIFs;
            }
        }
        return result;
    }
    static GetAssetNameCorrect(instrument, isBuy) {
        if (instrument == null) {
            return null;
        }
        if (instrument.TradingBalance == InstrumentTradingBalance.SETTLEMENT_IMMEDIATE) {
            return isBuy ? instrument.Exp2 : instrument.Exp1;
        }
        else {
            return instrument.Exp2;
        }
    }
    static IsEqualInstrument(i1, i2) {
        if (isNullOrUndefined(i1) || isNullOrUndefined(i2)) {
            return false;
        }
        if (i1 === i2) {
            return true;
        }
        if (i1.isHideRouteMode && i2.isHideRouteMode && i1.ShortName === i2.ShortName) {
            return true;
        }
        if (!isNullOrUndefined(i1.SourceName) &&
            !isNullOrUndefined(i2.SourceName) &&
            !isNullOrUndefined(i1.ExpDate) &&
            !isNullOrUndefined(i2.ExpDate) &&
            i1.SourceName === i2.SourceName &&
            i1.ExpDate.getTime() === i2.ExpDate.getTime()) {
            return true;
        }
        return false;
    }
    static IsEqualOptionInstruments(i1, i2) {
        if (isNullOrUndefined(i1) || isNullOrUndefined(i2)) {
            return false;
        }
        if (i1 === i2) {
            return true;
        }
        if (i1.Id !== i2.Id || i1.Route !== i2.Route) {
            return false;
        }
        if (i1.ForwardBaseInstrument !== i2.ForwardBaseInstrument) {
            return false;
        }
        return true;
    }
    static GetMonthsCountByCouponCycle(couponCycle) {
        switch (couponCycle) {
            case CouponCycle.Zero:
                return 0;
            case CouponCycle.ThreeMonth:
                return 3;
            case CouponCycle.SixMonth:
                return 6;
            case CouponCycle.Year:
                return 12;
            case CouponCycle.OneMonth:
            default:
                return 1;
        }
    }
    static FormatCouponCycle(cycle) {
        switch (cycle) {
            case CouponCycle.OneMonth:
                return Resources.getResource('fixedIncome.couponCycle.1m');
            case CouponCycle.ThreeMonth:
                return Resources.getResource('fixedIncome.couponCycle.3m');
            case CouponCycle.SixMonth:
                return Resources.getResource('fixedIncome.couponCycle.6m');
            case CouponCycle.Year:
                return Resources.getResource('fixedIncome.couponCycle.1y');
            case CouponCycle.Zero:
                return Resources.getResource('fixedIncome.couponCycle.zero');
            default:
                return '';
        }
    }
    updateDelayAndCanGetSnapshot(delay, canGetSnapshot, errorCode, subscribeType) {
        let changed = false;
        if (canGetSnapshot !== null && this.QuoteCanGetSnapshot !== canGetSnapshot) {
            this.QuoteCanGetSnapshot = canGetSnapshot;
            changed = true;
        }
        if (this.QuoteDelay.GetDelay(subscribeType) !== delay) {
            this.QuoteDelay.SetDelay(delay, subscribeType);
            changed = true;
        }
        if (this.HasLvl1SubscriptionErrorCode !== errorCode) {
            this.HasLvl1SubscriptionErrorCode = errorCode;
            changed = true;
        }
        if (changed) {
            this.RiskSettingsUpdated.Raise();
        }
    }
    GetDelayedIconTooltip() {
        let result = '';
        const delayValue = this.GetDelay();
        const delayedTooltipLocKey = 'TerceraInstrumentLookup.DelayedIco.tt';
        const specialTooltipLocKey = 'TerceraInstrumentLookup.DelayedIco.NotPluralDelay.tt'; // ключ с текстом для случая единственного числа слова "минут" ~ etc. Delayed for 1 minute но Delayed for 5 minutes (для рус.локализации поскольку есть еще доп. вариант окончания когда число оканчивается цифрами 2,3,4 минутЬІ, не добавлял доп. ключа, аналогично десктопу сократим минут до мин. - универсально для всех кейсов, кроме англ.и русс. переводов пока не добавлялось)
        if (delayValue > 0) {
            const needCommonLocKey = delayValue > 1 || !Resources.IsResourcePresent(specialTooltipLocKey); // для правильного окончания в случае задержки в одну минуту (а не Delayed for 1 minutes) (в русском еще сложнее - разн. окончания когда число заканчивается на 1 (минутУ), 2-3-4 (минутЬІ), 5-9,0 (минут) - потому пока аналогично десктопу в русс. один ключ локализации для всех случаев, в котором используется сокращение "мин." )
            const tooltipLocKey = needCommonLocKey ? delayedTooltipLocKey : specialTooltipLocKey;
            result = Resources.getResource(tooltipLocKey).replace('{0}', delayValue.toString());
        }
        return result;
    }
    GetDataSourceTradableId() {
        return this.DataSourceTradableId;
    }
    GetDataSourceRouteId() {
        return this.DataSourceRouteId;
    }
    IsInfoRouteForDataSourceAvailable() {
        // #102769
        if (this.DataSourceTradableId === -1 && this.DataSourceRouteId === -1 && !this.RoutesSupported.length) {
            return false;
        }
        return true;
    }
    IsDataSourceEnable() {
        if (!this.IsInfoRouteForDataSourceAvailable()) {
            return false;
        }
        return !!(this.DataSourceTradableId && this.DataSourceRouteId);
    }
    GetDelay(subscribeType = this.GetQuoteType()) {
        return this.QuoteDelay.GetDelay(subscribeType);
    }
    HasDelay(includeLevel2 = false) {
        if (this.QuoteDelay.HasDelay(HistoryType.QUOTE_LEVEL1)) {
            return true;
        }
        if (includeLevel2 && this.QuoteDelay.HasDelay(HistoryType.QUOTE_LEVEL2)) {
            return true;
        }
        return this.QuoteDelay.HasDelay(HistoryType.QUOTE_TRADES);
    }
    CanGetSnapshot() {
        return this.QuoteCanGetSnapshot;
    }
    IsAmericanExerciseStyle() {
        return this.ExerciseStyle === ExerciseStyle.AMERICAN;
    }
    GetLocalizedExerciseStyle() {
        let localKey = null;
        switch (this.ExerciseStyle) {
            case ExerciseStyle.EUROPEAN:
                localKey = 'Instrument.ExerciseStyle.EUROPEAN';
                break;
            case ExerciseStyle.AMERICAN:
                localKey = 'Instrument.ExerciseStyle.AMERICAN';
                break;
        }
        return Resources.getResource(localKey);
    }
    IsShowVolume() {
        return this.HistoryType === HistoryType.QUOTE_TRADES;
    }
    GetQuoteType(historyType = null) {
        let subsType = historyType !== null && historyType !== void 0 ? historyType : this.HistoryType;
        if (subsType === HistoryType.ASK) {
            subsType = HistoryType.BID;
        }
        return subsType;
    }
    GetInteriorID(continuousContractSaveMode = undefined) {
        if (isValidString(this._cachedInteriorID)) {
            return this._cachedInteriorID;
        }
        let resultSTR = '';
        if (this.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            let strID = this.InstrumentTradableID + CONTINUOUS_CONTRACT_POSTFIX + InstrumentUtils.SEPARATOR + this.Route;
            if (continuousContractSaveMode || GlobalFlags.InstrumentSaveMode) {
                resultSTR = strID += '|' + this.Id;
            }
            else {
                resultSTR = strID;
            }
        }
        else {
            resultSTR = this.InstrumentTradableID + InstrumentUtils.SEPARATOR + this.Route;
        }
        this._cachedInteriorID = resultSTR;
        return this._cachedInteriorID;
    }
    static IsWorkingInstrument(insRef) {
        if (!insRef || insRef.IsEmpty)
            return false;
        return true;
    }
    GetExtInsType() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.INSTRUMENT_TYPE]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.INSTRUMENT_TYPE];
        }
        return value;
    }
    GetExtSymbol() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.SYMBOL]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.SYMBOL];
        }
        return value;
    }
    GetExtToken() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TOKEN]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TOKEN];
        }
        return value;
    }
    GetExtMarketType() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.MARKET_TYPE]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.MARKET_TYPE];
        }
        return value;
    }
    GetExtSeries() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.SERIES]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.SERIES];
        }
        return value;
    }
    GetExtTradingUnit() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TRADINGUNIT]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TRADINGUNIT];
        }
        return value;
    }
    GetExtPriceQuoteUnit() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.PRICEQUOTEUNIT]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.PRICEQUOTEUNIT];
        }
        return value;
    }
    GetExtDeliveryUnit() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.DELIVERYUNIT]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.DELIVERYUNIT];
        }
        return value;
    }
    GetExtLotSize() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.LOTSIZE]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.LOTSIZE];
        }
        return value;
    }
    GetExtTenderPeriodStartDate() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TENDERPERIODSTARTDATE]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TENDERPERIODSTARTDATE];
        }
        return value;
    }
    GetExtTenderPeriodEndDate() {
        let value = '';
        if (this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TENDERPERIODENDDATE]) {
            value = this.InstrumentAdditionalInfo[EXT_INSTRUMENT_MESSAGE.TENDERPERIODENDDATE];
        }
        return value;
    }
    GetExtSMFlags() {
        // also receive it using extended field groups, storaging in InstrumentAdditionalInfo #114474
        const EXT = EXT_INSTRUMENT_MESSAGE;
        const possibleFlags = [EXT.FLAG_GSM, EXT.FLAG_ESM, EXT.FLAG_LONG_ASM, EXT.FLAG_SHORT_ASM];
        const result = {};
        for (let i = 0; i < possibleFlags.length; i++) {
            const flag = possibleFlags[i];
            const flagValue = this.InstrumentAdditionalInfo[flag];
            if (Instrument.ShownSMFlagValue(flagValue, flag)) {
                result[flag] = flagValue;
            }
        }
        return result;
    }
    GetExtSMFlagByKey(flagKey) {
        // #114474
        const splitArr = flagKey.split('.');
        const flag = splitArr.length ? splitArr[splitArr.length - 1] : flagKey;
        const flagValue = this.InstrumentAdditionalInfo[flag] || null;
        if (!Instrument.ShownSMFlagValue(flagValue, flag)) {
            return '';
        }
        const localKey = 'FlagSM.' + flag + '.' + flagValue;
        return Resources.getResource(localKey);
    }
    static ShownSMFlagValue(flagValue, flag) {
        if (!flagValue || !flag) {
            return false;
        }
        const flagExistInLocale = Resources.IsResourcePresent('FlagSM.' + flag + '.' + flagValue); // #115760
        return flagExistInLocale; // && flagValue != '1100' && flagValue != '20';
    }
    GetMarketPrice(account, side) {
        if (MathUtils.IsNullOrUndefined(account) || MathUtils.IsNullOrUndefined(side)) {
            return null;
        }
        const isBuy = side == OperationType.Buy;
        const askOrBid = isBuy ? 'AskSpread_SP_Ins' : 'BidSpread_SP_Ins';
        const quote = this.GetLastQuote(QuoteValid.Last);
        if (quote) {
            return quote[askOrBid](this.DataCache.GetSpreadPlan(account), this);
        }
        return null;
    }
    static GetFutureMonthCode(month) {
        switch (month) {
            case 1:
                return 'F';
            case 2:
                return 'G';
            case 3:
                return 'H';
            case 4:
                return 'J';
            case 5:
                return 'K';
            case 6:
                return 'M';
            case 7:
                return 'N';
            case 8:
                return 'Q';
            case 9:
                return 'U';
            case 10:
                return 'V';
            case 11:
                return 'X';
            case 12:
                return 'Z';
        }
        return '';
    }
    GetDisclosedQuantityMinCoefficient() {
        const instrumentType = this.DataCache.getInstrumentTypeById(this.TypeId);
        if (isNullOrUndefined(instrumentType)) {
            return null;
        }
        return instrumentType.DisclosedQuantityMinCoefficient();
    }
    GetWarningForChangeFromLastPrice(ranges) {
        if (!(ranges === null || ranges === void 0 ? void 0 : ranges.length)) {
            return null;
        }
        return new WarningForChangeFromLastPrice(ranges);
    }
    GeneratedInstrumentDayBarMessages() {
        const now = DateTimeUtils.DateTimeNow();
        const tradableId = this.InstrumentTradableID;
        const routeId = this.Route;
        const instrumentId = this.GetInteriorID();
        // по трейдам
        const idbmTrade = new DirectInstrumentDayBarMessage();
        idbmTrade.InstrumentBarType = HistoryType.QUOTE_TRADES;
        idbmTrade.TradableId = tradableId;
        idbmTrade.QuoteRouteId = routeId;
        idbmTrade.Time = now;
        idbmTrade.TargetInstrumentName = instrumentId;
        if (!isNaN(this.Open)) {
            idbmTrade.Open = this.Open;
        }
        if (!isNaN(this.High)) {
            idbmTrade.High = this.High;
        }
        if (!isNaN(this.Low)) {
            idbmTrade.Low = this.Low;
        }
        if (!isNaN(this.Close)) {
            idbmTrade.PreviousClosePrice = this.Close;
        }
        if (!isNaN(this.MainClose)) {
            idbmTrade.TodayClosePrice = this.MainClose;
        }
        if (this.Ticks !== 0) {
            idbmTrade.Ticks = this.Ticks;
        }
        if (!isNaN(this.TotalVolume)) {
            idbmTrade.Volume = this.TotalVolume;
        }
        idbmTrade.LastTime = this.Level1.getLastMessageTime;
        let last = NaN;
        if (!isNullOrUndefined(this.Level1.lastPriceQuote)) {
            last = this.Level1.GetLastPrice(null);
        }
        if (!isNaN(last)) {
            idbmTrade.LastPrice = last;
        }
        const lastSize = this.Level1.GetLastSize(false);
        if (!isNaN(lastSize)) {
            idbmTrade.LastSize = lastSize;
        }
        // if (!string.IsNullOrEmpty(tradeVenue))
        //     idbmTrade.LastVenue = tradeVenue;
        // по аскам
        const idbmAsk = new DirectInstrumentDayBarMessage();
        idbmAsk.InstrumentBarType = HistoryType.QUOTE_ASK;
        idbmAsk.TradableId = tradableId;
        idbmAsk.QuoteRouteId = routeId;
        idbmAsk.Time = now;
        idbmAsk.TargetInstrumentName = instrumentId;
        if (!isNaN(this.AskOpen)) {
            idbmAsk.Open = this.AskOpen;
        }
        if (!isNaN(this.AskHigh)) {
            idbmAsk.High = this.AskHigh;
        }
        if (!isNaN(this.AskLow)) {
            idbmAsk.Low = this.AskLow;
        }
        if (!isNaN(this.AskPrevClose)) {
            idbmAsk.PreviousClosePrice = this.AskPrevClose;
        }
        if (!isNaN(this.AskMainClose)) {
            idbmAsk.TodayClosePrice = this.AskMainClose;
        }
        // по бидам
        const idbmBid = new DirectInstrumentDayBarMessage();
        idbmBid.InstrumentBarType = HistoryType.QUOTE_LEVEL1;
        idbmBid.TradableId = tradableId;
        idbmBid.QuoteRouteId = routeId;
        idbmBid.Time = now;
        idbmBid.TargetInstrumentName = instrumentId;
        if (!isNaN(this.BidOpen)) {
            idbmBid.Open = this.BidOpen;
        }
        if (!isNaN(this.BidHigh)) {
            idbmBid.High = this.BidHigh;
        }
        if (!isNaN(this.BidLow)) {
            idbmBid.Low = this.BidLow;
        }
        if (!isNaN(this.BidPrevClose)) {
            idbmBid.PreviousClosePrice = this.BidPrevClose;
        }
        if (!isNaN(this.BidMainClose)) {
            idbmBid.TodayClosePrice = this.BidMainClose;
        }
        let mainIDBM;
        if (this.HistoryType == HistoryType.QUOTE_TRADES) {
            mainIDBM = idbmTrade;
        }
        else if (this.HistoryType == HistoryType.QUOTE_ASK) {
            mainIDBM = idbmAsk;
        }
        else {
            mainIDBM = idbmBid;
        }
        mainIDBM.OpenInterest = this.OpenInterest;
        mainIDBM.SettlementPrice = this.SettlementPrice;
        mainIDBM.PrevSettlementPrice = this.PrevSettlementPrice;
        mainIDBM.FiftyTwoWeekHighPrice = this.FiftyTwoWeekHighPrice;
        mainIDBM.FiftyTwoWeekLowPrice = this.FiftyTwoWeekLowPrice;
        mainIDBM.High13Week = this.High13Week;
        mainIDBM.Low13Week = this.Low13Week;
        mainIDBM.High26Week = this.High26Week;
        mainIDBM.Low26Week = this.Low26Week;
        return [idbmAsk, idbmBid, idbmTrade];
    }
    UseWarningForChangeFromLastPrice() {
        return !!this.WarningForChangeFromLastPrice;
    }
    GetChangeByPrice(price) {
        // https://tp.traderevolution.com/entity/108864
        return this.WarningForChangeFromLastPrice ? this.WarningForChangeFromLastPrice.GetChangeByPrice(price) : null;
    }
    GetTickCost() {
        // TODO
        let ticCost = this.QuotingType === QuotingType.TickCost_TickSize && this.FuturesTickCoast > 0
            ? this.FuturesTickCoast / this.PipsSize
            : 1.0;
        ticCost *= this.ContractMultiplier;
        return ticCost;
    }
    getMinOffset() {
        let offsetType = BaseSettings.offsetType;
        if (offsetType === OffsetModeViewEnum.TicksFractionalForForex &&
            !ticksUtils.AllowUseFractinalTicksForForex(this.InstrType)) {
            offsetType = OffsetModeViewEnum.Ticks;
        }
        const minOffsetValue = offsetType === OffsetModeViewEnum.Percent ? 0.01 : 1;
        return SlTpUtils.toVisualValue(minOffsetValue, this, offsetType);
    }
    getOffsetPrecision() {
        var _a;
        const minOffset = this.getMinOffset();
        const decimalPlaces = ((_a = minOffset.toString().split('.')[1]) === null || _a === void 0 ? void 0 : _a.length) || 0;
        return decimalPlaces;
    }
    equals(instrument) {
        return this === instrument;
    }
    get instrumentTypeShort() {
        return SymbolLookupViewItemHelper.getInstrumentTypeShortForInstrument(this);
    }
    get instrumentTypeColor() {
        return SymbolLookupViewItemHelper.getInstrumentTypeColorForInstrument(this);
    }
    AllowOptionsExerciseOnLastTradeDate() {
        var _a;
        const sessionPeriod = (_a = this.Session.CurrentSessionPeriod) !== null && _a !== void 0 ? _a : this.Session.FindSessionPeriod(new Date());
        if (sessionPeriod == null || this.LastTradeDate == null)
            return false;
        const sameDay = this.Session.isServerTodayDate(this.LastTradeDate);
        if (sessionPeriod.allowOptionsExerciseOnLastTradeDate && sameDay)
            return true;
        return false;
    }
    ApplySession(session) {
        this.Session = session;
    }
    GetFaceValueMultiplier() {
        if (this.InstrType !== InstrumentTypes.FIXED_INCOME ||
            this.PricingType !== FixedIncomePricingType.PercentFromFaceValue) {
            return 1;
        }
        return this.FaceValue / 100;
    }
    IsCommentEnabled(orderType) {
        return (orderType === null || orderType === void 0 ? void 0 : orderType.id()) === OrderType.Care && !!this.EnableComment;
    }
}
Instrument.Delivery_Physical = 0;
Instrument.Delivery_Cash = 1;
Instrument.FAKE_NONFIXED_RESPONSE = 'fakeNonFixedInstrument';
Instrument.saveMode = false;
